import { useEffect, useState } from "react";
import { Typography, Paper, Container, Grid, Tabs, Tab, Alert, TextField, IconButton, CircularProgress, Snackbar, Button, Stack, Chip, Dialog, DialogContent, Avatar, MenuItem, DialogActions, DialogTitle, DialogContentText, Box, Card, FormControl, InputLabel, Select, SelectChangeEvent, ListItemIcon, ListItemText, Tooltip, FormControlLabel, Switch } from "@mui/material";
import { createTheme } from '@mui/material/styles';
import { TabContext, TabPanel } from "@mui/lab";
import ILog from "../../types/Log";
import LogService from "../../services/LogService";
import Empleados from "./Empleados";
import ConfigUtils from "../../utils/Config";
import EditIcon from '@mui/icons-material/Edit';
import OkIcon from '@mui/icons-material/CheckCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import Utilities from "../../common/Utilities";
import IAlert from "../../types/Alert";
import AuthService from "../../services/AuthService";
import { CONFIGTYPES } from "../../types/Config";
import { useNavigate } from "react-router-dom";
import IActividad from "../../types/Actividad";
import * as constantes from "../../common/Constantes";
import ConfigService from "../../services/ConfigService";
import ISala from "../../types/Sala";
import { CountryCode, parsePhoneNumberFromString } from "libphonenumber-js";
import AgendaUtils from "../../utils/Agenda";
import Holidays from "./Holidays";
import { isMobile } from "react-device-detect";
import Mail from "./Mail";
import JA from "./JA";
import CI from "./CI";
import WavesIcon from '@mui/icons-material/Waves';
import LandscapeIcon from '@mui/icons-material/Landscape';
import DiamondIcon from '@mui/icons-material/Diamond';
import GrassIcon from '@mui/icons-material/Grass';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ImageUploader from "../utilities/ImageUploader";
import ColorPicker from "../utilities/ColorPicker";
import { emitChange } from "../../utils/LogoEvents";
import Servicios from "./Servicios";
import { getVersion } from "../../services/VersionService";
import { APP_VERSION } from "../../version";
import ConfigAgenda from "./ConfigAgenda";

interface ConfigProps {
    selectedTab?: string;
    alert?: IAlert;
}

const Config = (props: ConfigProps) => {
    const theme = createTheme();
    const history = useNavigate();
    const navigate = useNavigate();
    const [apiVersion, setApiVersion] = useState<string>("");

    const [alert, setAlert] = useState<IAlert>(props.alert ? props.alert : { open: false, severity: 'info', msg: '' });
    const [value, setValue] = useState<string>(props.selectedTab ? props.selectedTab : "1");
    const [editClinicData, setEditClinicData] = useState(false);
    const [clinicName, setClinicName] = useState<string>(ConfigUtils.getClinicName());
    const [clinicCity, setClinicCity] = useState<string>(ConfigUtils.getClinicCity());
    const [clinicDomicilio, setClinicDomicilio] = useState<string>(ConfigUtils.getClinicDomicilio());
    const [clinicEmail, setClinicEmail] = useState<string>(ConfigUtils.getClinicEmail());
    const [clinicTelefono, setClinicTelefono] = useState<string>(ConfigUtils.getClinicTelefono());
    const paisClinica = ConfigUtils.getPais() || 'ES';
    const provincias = AgendaUtils.getProvincias(paisClinica);
    const [clinicProvincia, setProvincia] = useState<string>(ConfigUtils.getProvincia());
    const [modalEditProvincia, setModalEditProvincia] = useState(false);

    // Si el país de la clínica no tiene datos de provincia (el selector está oculto), no debe
    // quedarse guardada una provincia de un país anterior — se limpia en cuanto se detecta.
    useEffect(() => {
        if (provincias.length === 0 && clinicProvincia !== '') {
            setProvincia('');
        }
    }, [provincias, clinicProvincia]);

    const [citaReminder, setCitaReminder] = useState<Boolean>(ConfigUtils.getCitaReminder() ?? false);
    const [openReminderDialog, setOpenReminderDialog] = useState(false);
    const [pendingReminderValue, setPendingReminderValue] = useState<boolean | null>(null);
    const [loadingReminder, setLoadingReminder] = useState(false);

    const [waEnabled, setWaEnabled] = useState<Boolean>(ConfigUtils.getWaConfig() ?? false);
    const [openWaDialog, setOpenWaDialog] = useState(false);
    const [pendingWaValue, setPendingWaValue] = useState<boolean | null>(null);
    const [loadingWa, setLoadingWa] = useState(false);

    //ACTIVIDADES
    const [actividades, setActividades] = useState<Array<IActividad>>(ConfigUtils.getActividades());
    const [modalActividad, setModalActividad] = useState(false);
    const [loadingActividad, setLoadingActividad] = useState(false);
    const [actividadName, setActividadName] = useState<string>('');
    const [duracion, setDuracion] = useState<number>(60);
    const [capacidad, setCapacidad] = useState<number>(1);
    const [isEditActividad, setIsEditActividad] = useState(false);
    const [modalDeleteActividad, setModalDeleteActividad] = useState(false);
    const [selectedActividad, setSelectedActividad] = useState<IActividad>();

    //SALAS
    const [salas, setSalas] = useState<Array<ISala>>(ConfigUtils.getSalas());
    const [modalSala, setModalSala] = useState(props.selectedTab === "2" ? true : false);
    const [loadingSala, setLoadingSala] = useState(false);
    const [salaName, setSalaName] = useState<string>('');
    const [salaActs, setSalaActs] = useState<Array<string>>([]);
    const [salaIcon, setSalaIcon] = useState<string>('');
    const [isEditSala, setIsEditSala] = useState(false);
    const [modalDeleteSala, setModalDeleteSala] = useState(false);
    const [selectedSala, setSelectedSala] = useState<ISala>();

    const [clinicLogo, setClinicLogo] = useState<string | null>(ConfigUtils.getClinicLogo());
    const [clinicColor, setClinicColor] = useState<string>(ConfigUtils.getClinicColor());
    const [loadingStyle, setLoadingStyle] = useState(false);

    const userRole: number = ConfigUtils.getUserRole();

    useEffect(() => {
        const fetchVersion = async () => {
            try {
                const res = await getVersion();
                setApiVersion(res.data.apiVersion);
            } catch {
                setApiVersion("unknown");
            }
        };  

        fetchVersion();
    }, []);

    const handleCloseAlert = (_?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlert({ open: false, severity: 'info', msg: '' });
    };

    const handleChange = (_: React.SyntheticEvent, newValue: string) => {
        setValue(newValue);
    };

    const updateClinicData = (validateProvincia: boolean) => {
        if (Utilities.isNullEmptyUndefined(clinicName)) {
            setAlert({ open: true, severity: 'warning', msg: 'El nombre de la clínica está vacío' });
            return;
        }
        if (Utilities.isNullEmptyUndefined(clinicCity)) {
            setAlert({ open: true, severity: 'warning', msg: 'La ciudad de la clínica está vacía' });
            return;
        }
        if (Utilities.isNullEmptyUndefined(clinicDomicilio)) {
            setAlert({ open: true, severity: 'warning', msg: 'La dirección de la clínica está vacía' });
            return;
        }
        if (Utilities.isNullEmptyUndefined(clinicEmail)) {
            setAlert({ open: true, severity: 'warning', msg: 'El email de la clínica está vacío' });
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clinicEmail)) {
            setAlert({ open: true, severity: 'warning', msg: 'El email no tiene un formato válido' });
            return;
        }
        if (Utilities.isNullEmptyUndefined(clinicTelefono)) {
            setAlert({ open: true, severity: 'warning', msg: 'El teléfono de la clínica está vacío' });
            return;
        }
        // Fuera de España se acepta cualquier formato (espacios, prefijo, guiones), pero se guarda
        // normalizado a solo dígitos locales, igual que ya se hace siempre para España, para que
        // se muestre de forma consistente donde se incrusta como texto (WhatsApp, email, CI...).
        let telefonoNormalizado = clinicTelefono;
        if (paisClinica === 'ES') {
            if (!/^\d{9}$/.test(clinicTelefono)) {
                setAlert({ open: true, severity: 'warning', msg: 'El teléfono no tiene un formato válido' });
                return;
            }
        } else {
            const parsed = parsePhoneNumberFromString(clinicTelefono, paisClinica as CountryCode);
            if (!parsed?.isValid()) {
                setAlert({ open: true, severity: 'warning', msg: 'El teléfono no tiene un formato válido' });
                return;
            }
            telefonoNormalizado = parsed.nationalNumber;
        }
        if (validateProvincia) {
            if (clinicProvincia !== ConfigUtils.getProvincia()) {
                setModalEditProvincia(true);
                return;
            }
        } else {
            setModalEditProvincia(false);
        }

        AuthService.saveClinicData(ConfigUtils.getUserId(), { clinicName, clinicProvincia, clinicCity, clinicDomicilio, clinicEmail, clinicTelefono: telefonoNormalizado })
        .then((response: any) => {
            ConfigUtils.editConfig(CONFIGTYPES.clinicName.toString(), clinicName);
            ConfigUtils.editConfig(CONFIGTYPES.clinicCity.toString(), clinicCity);
            ConfigUtils.editConfig(CONFIGTYPES.clinicDomicilio.toString(), clinicDomicilio);
            ConfigUtils.editConfig(CONFIGTYPES.clinicEmail.toString(), clinicEmail);
            ConfigUtils.editConfig(CONFIGTYPES.clinicTelefono.toString(), telefonoNormalizado);
            setClinicTelefono(telefonoNormalizado);
            if (clinicProvincia !== ConfigUtils.getProvincia()) {
                ConfigUtils.deleteConfig(CONFIGTYPES.holidays.toString(), null);
            }
            ConfigUtils.editConfig(CONFIGTYPES.clinicProvincia.toString(), clinicProvincia);
            setAlert({ open: true, severity: 'success', msg: response.data.message });
        })
        .catch((e: any) => {
            if (e.response.status === 401) {
                localStorage.clear();
                history(constantes.R_HOME);
            } else {
                const error: ILog = { method: 'Config.Component.saveClinicData', level: 'error', message: e.message, meta: e.response };
                LogService.logError(error);
                setAlert({ open: true, severity: 'error', msg: e.response.data.message });
            }
        })
        .finally(() => {
            setEditClinicData(false);
        });
    } 

    const closeModalActividad = () => {
        setActividadName('');
        setDuracion(60);
        setCapacidad(1);
        setSelectedActividad(undefined);
        setIsEditActividad(false);
        setModalActividad(false);
    };

    const setIsUpdateActividad = (actividad: IActividad) => {
        setIsEditActividad(true);
        setSelectedActividad(actividad);
        setActividadName(actividad.title);
        setDuracion(actividad.duracion);
        setCapacidad(actividad.capacity);
        setModalActividad(true);
    };

    const saveActividad = () => {
        setLoadingActividad(true);

        if (Utilities.isNullEmptyUndefined(actividadName)) {
            setAlert({ open: true, severity: 'warning', msg: 'Es necesario poner un nombre a la actividad' });
            setLoadingActividad(false);
            return;
        }

        const act: IActividad = {
            title: actividadName,
            duracion,
            capacity: capacidad,
            userId: ConfigUtils.getUserId()
        };

        ConfigService.createActividad(act)
            .then((response: any) => {
                const newActividad: IActividad = response.data;
                ConfigUtils.addConfig(CONFIGTYPES.actividades.toString(), newActividad);
                const newActividades: IActividad[] = [...actividades, newActividad];
                setActividades(newActividades);
                setAlert({ open: true, severity: 'success', msg: 'Actividad guardada correctamente' });
                window.dispatchEvent(new Event('popstate'));
            })
            .catch((e: any) => {
                if (e.response.status === 401) {
                    localStorage.clear();
                    history(constantes.R_HOME);
                } else {
                    const error: ILog = { method: 'Config.Component.saveActividad', level: 'error', message: e.message, meta: e.response };
                    LogService.logError(error);
                    setAlert({ open: true, severity: 'error', msg: e.response.data.message });
                }
            })
            .finally(() => {
                setLoadingActividad(false);
                closeModalActividad();
            });
    };

    const updateActividad = () => {
        if (!selectedActividad || !selectedActividad._id) return;

        setLoadingActividad(true);

        const updatedActividad: IActividad = {
            userId: selectedActividad?.userId,
            title: actividadName,
            duracion,
            capacity: capacidad
        };

        ConfigService.updateActividad(selectedActividad._id, updatedActividad)
            .then((response: any) => {
                const newActividades: IActividad[] = [response.data, ...actividades.filter((e) => e._id !== response.data._id)];
                setActividades(newActividades);
                ConfigUtils.editConfig(CONFIGTYPES.actividades.toString(), response.data);
                setAlert({ open: true, severity: 'success', msg: 'Actividad actualizada correctamente' });
            })
            .catch((e: any) => {
                if (e.response.status === 401) {
                    localStorage.clear();
                    history(constantes.R_HOME);
                } else {
                    const error: ILog = { method: 'Config.Component.updateOutOffice', level: 'error', message: e.message, meta: e.response };
                    LogService.logError(error);
                    setAlert({ open: true, severity: 'error', msg: e.response.data.message });
                }
            })
            .finally(() => {
                setLoadingActividad(false);
                closeModalActividad();
            });
    };

    const deleteActividad = () => {
        if (!selectedActividad || !selectedActividad._id) return;

        ConfigService.deleteActividad(selectedActividad?._id)
            .then((_: any) => {
                const newActividades: IActividad[] = [...actividades.filter((e) => e._id !== selectedActividad._id)];
                setActividades(newActividades);
                const newSalas = ConfigUtils.updateSalas(salas, selectedActividad?._id);
                setSalas(newSalas);
                ConfigUtils.deleteConfig(CONFIGTYPES.actividades.toString(), selectedActividad._id);
                ConfigUtils.editConfig(CONFIGTYPES.salas.toString(), newSalas);
                setAlert({ open: true, severity: 'success', msg: 'Actividad eliminada correctamente' });
            })
            .catch((e: any) => {
                if (e.response.status === 401) {
                    localStorage.clear();
                    history(constantes.R_HOME);
                } else {
                    const error: ILog = { method: 'Config.Component.deleteActividad', level: 'error', message: e.message, meta: e.response };
                    LogService.logError(error);
                    setAlert({ open: true, severity: 'error', msg: e.response.data.message });
                }
            })
            .finally(() => {
                setSelectedActividad(undefined);
                setModalDeleteActividad(false);
            });
    };

    const closeModalSala = () => {
        setSalaName('');
        setSalaIcon('');
        setSalaActs([]);
        setSelectedSala(undefined);
        setIsEditSala(false);
        setModalSala(false);
    };

    const setIsUpdateSala = (sala: ISala) => {
        setIsEditSala(true);
        setSelectedSala(sala);
        setSalaName(sala.title);
        setSalaIcon(sala.icon != undefined ? sala.icon : "");
        setSalaActs(sala.actividades);
        setModalSala(true);
    };

    const handleChangeSala = (event: SelectChangeEvent<typeof salaActs>) => {
        const { target: { value }, } = event;
        setSalaActs(
            // On autofill we get a stringified value.
            typeof value === 'string' ? value.split(',') : value,
        );

    };

    const handleChangeIcon = (event: SelectChangeEvent) => {
        setSalaIcon(event.target.value);
    };

    const saveSala = () => {
        setLoadingSala(true);

        if (Utilities.isNullEmptyUndefined(salaName)) {
            setAlert({ open: true, severity: 'warning', msg: 'Es necesario poner un nombre a la sala' });
            setLoadingSala(false);
            return;
        }

        const sala: ISala = {
            title: salaName,
            icon: salaIcon,
            actividades: salaActs,
            userId: ConfigUtils.getUserId()
        };

        ConfigService.createSala(sala)
            .then((response: any) => {
                const newSala: ISala = response.data;
                ConfigUtils.addConfig(CONFIGTYPES.salas.toString(), newSala);
                const newSalas: ISala[] = [...salas, newSala];
                setSalas(newSalas);
                setAlert({ open: true, severity: 'success', msg: 'Sala guardada correctamente' });
            })
            .catch((e: any) => {
                if (e.response.status === 401) {
                    localStorage.clear();
                    history(constantes.R_HOME);
                } else {
                    const error: ILog = { method: 'Config.Component.saveSala', level: 'error', message: e.message, meta: e.response };
                    LogService.logError(error);
                    setAlert({ open: true, severity: 'error', msg: e.response.data.message });
                }
            })
            .finally(() => {
                setLoadingSala(false);
                closeModalSala();
            });
    };

    const updateSala = () => {
        if (!selectedSala || !selectedSala._id) return;

        setLoadingSala(true);

        const updatedSala: ISala = {
            userId: selectedSala?.userId,
            title: salaName,
            icon: salaIcon,
            actividades: salaActs
        };

        ConfigService.updateSala(selectedSala._id, updatedSala)
            .then((response: any) => {
                const newSalas: ISala[] = [response.data, ...salas.filter((e) => e._id !== response.data._id)];
                setSalas(newSalas);
                ConfigUtils.editConfig(CONFIGTYPES.salas.toString(), response.data);
                setAlert({ open: true, severity: 'success', msg: 'Sala actualizada correctamente' });
            })
            .catch((e: any) => {
                if (e.response.status === 401) {
                    localStorage.clear();
                    history(constantes.R_HOME);
                } else {
                    const error: ILog = { method: 'Config.Component.updateSala', level: 'error', message: e.message, meta: e.response };
                    LogService.logError(error);
                    setAlert({ open: true, severity: 'error', msg: e.response.data.message });
                }
            })
            .finally(() => {
                setLoadingSala(false);
                closeModalSala();
            });

    };

    const deleteSala = () => {
        if (!selectedSala || !selectedSala._id) return;

        ConfigService.deleteSala(selectedSala?._id)
            .then((response: any) => {
                const newSalas: ISala[] = [...salas.filter((e) => e._id !== selectedSala._id)];
                setSalas(newSalas);
                ConfigUtils.deleteConfig(CONFIGTYPES.salas.toString(), selectedSala._id);
                setAlert({ open: true, severity: 'success', msg: 'Sala eliminada correctamente' });
            })
            .catch((e: any) => {
                if (e.response.status === 401) {
                    localStorage.clear();
                    history(constantes.R_HOME);
                } else {
                    const error: ILog = { method: 'Config.Component.deleteSala', level: 'error', message: e.message, meta: e.response };
                    LogService.logError(error);
                    setAlert({ open: true, severity: 'error', msg: e.response.data.message });
                }
            })
            .finally(() => {
                setSelectedSala(undefined);
                setModalDeleteSala(false);
            });
    };

    const closeModalProvincia = () => {
        setProvincia(ConfigUtils.getProvincia());
        setModalEditProvincia(false);
    };

    const saveClinicStyle = () => {
        setLoadingStyle(true);
        AuthService.saveClinicStyle(ConfigUtils.getUserId(), { clinicLogo, clinicColor })
        .then((response: any) => {
            ConfigUtils.editConfig(CONFIGTYPES.clinicLogo.toString(), clinicLogo);
            ConfigUtils.editConfig(CONFIGTYPES.clinicColor.toString(), clinicColor);
            emitChange(clinicLogo);
            setAlert({ open: true, severity: 'success', msg: response.data.message });
        })
        .catch((e: any) => {
            if (e.response.status === 401) {
                localStorage.clear();
                history(constantes.R_HOME);
            } else {
                const error: ILog = { method: 'Config.Component.saveClinicStyle', level: 'error', message: e.message, meta: e.response };
                LogService.logError(error);
                setAlert({ open: true, severity: 'error', msg: e.response.data.message });
            }
        })
        .finally(() => {
            setLoadingStyle(false);
        });

    };

    const updateReminderConfig = async (enabled: boolean) => {
        try {
          setLoadingReminder(true);
          await ConfigService.updateReminder(ConfigUtils.getUserId(), { enabled });
          setCitaReminder(enabled);
          setAlert({ open: true, severity: "success", msg: "Configuración actualizada" });
          ConfigUtils.editConfig(CONFIGTYPES.reminders, enabled);
        } catch (e: any) {
          if (e.response?.status === 401) {
            localStorage.clear();
            history(constantes.R_HOME);
          } else {
            const error: ILog = {
              method: "Config.Component.updateReminderConfig",
              level: "error",
              message: e.message,
              meta: e.response,
            };
            LogService.logError(error);
            setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
          }
        } finally {
          setLoadingReminder(false);
        }
    };

    const updateWaConfig = async (enabled: boolean) => {
      try {
        setLoadingWa(true);
        await ConfigService.updateWaConfig(ConfigUtils.getUserId(), { enabled });
        setWaEnabled(enabled);
        setAlert({ open: true, severity: "success", msg: "Configuración actualizada" });
        ConfigUtils.editConfig(CONFIGTYPES.waConfig, enabled);
        window.dispatchEvent(new Event('popstate'));
      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.clear();
          history(constantes.R_HOME);
        } else {
          const error: ILog = {
            method: "Config.Component.updateWaConfig",
            level: "error",
            message: e.message,
            meta: e.response,
          };
          LogService.logError(error);
          setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
        }
      } finally {
        setLoadingWa(false);
      }
    };

    try {
        return (
          userRole == constantes.ROLES.ADMINISTRADOR ?
          <Grid>
            <Grid container>
              <Grid item md={12}>
                <Container>
                  <Paper elevation={3} sx={{ mt: 2, p: 3, mb: 3 }}>

                    <Grid item xs={12}>
                      <Typography variant="h4" sx={{ mt: 1, mb: 1, fontWeight: 'bold' }}>
                        CONFIGURACIÓN
                      </Typography>
                    </Grid>

                    <TabContext value={value}>
                      {isMobile ? (
                        <FormControl fullWidth sx={{ mb: 2 }}>
                          <InputLabel>Sección</InputLabel>
                          <Select
                            value={value}
                            label="Sección"
                            onChange={(e) => setValue(e.target.value)}
                            size="small"
                          >
                            <MenuItem value="1">Usuarios</MenuItem>
                            <MenuItem value="2">Clínica</MenuItem>
                            <MenuItem value="3">Agenda</MenuItem>
                            <MenuItem value="4">Facturación</MenuItem>
                            <MenuItem value="5">Festivos</MenuItem>
                            <MenuItem value="6">Correo</MenuItem>
                            <MenuItem value="7">Justificante asistencia</MenuItem>
                            <MenuItem value="8">Consentimiento informado</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        <Tabs
                          sx={{ maxWidth: { md: 800, lg: 1050 } }}
                          variant="scrollable"
                          scrollButtons
                          allowScrollButtonsMobile
                          value={value}
                          onChange={handleChange}
                        >
                          <Tab label="Usuarios" value="1" />
                          <Tab label="Clínica" value="2" />
                          <Tab label="Agenda" value="3" />
                          <Tab label="Facturación" value="4" />
                          <Tab label="Festivos" value="5" />
                          <Tab label="Correo" value="6" />
                          <Tab label="Justificante asistencia" value="7" />
                          <Tab label="Consentimiento informado" value="8" />
                        </Tabs>
                      )}

                      <TabPanel value="1">
                        <Empleados />
                      </TabPanel>
                  
                      <TabPanel value="2">
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={7}>
                          <Stack spacing={3}>

                            {/* DATOS CLÍNICA */}
                            <Card sx={{ p: 3 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" fontWeight="bold">
                                  Datos de la clínica
                                </Typography>
                                <Tooltip title={editClinicData ? "Guardar" : "Editar"}>
                                  <IconButton onClick={() => editClinicData ? updateClinicData(true) : setEditClinicData(true)}>
                                    {editClinicData ? <OkIcon /> : <EditIcon />}
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <TextField
                                    fullWidth
                                    value={clinicName}
                                    onChange={(e) => setClinicName(e.target.value)}
                                    variant="standard"
                                    label="Nombre de la clínica"
                                    inputProps={{ readOnly: !editClinicData }}
                                  />
                                </Grid>
                                {provincias.length > 0 &&
                                <Grid item xs={12} md={6}>
                                  <TextField
                                    select
                                    fullWidth
                                    value={clinicProvincia}
                                    onChange={(e) => setProvincia(e.target.value)}
                                    variant="standard"
                                    label="Provincia"
                                    inputProps={{ readOnly: !editClinicData }}
                                  >
                                    {provincias.map((provincia) => (
                                      <MenuItem key={provincia.id} value={provincia.id}>
                                        {provincia.name}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                </Grid>}
                                  
                                <Grid item xs={12} md={6}>
                                  <TextField
                                    fullWidth
                                    value={clinicCity}
                                    onChange={(e) => setClinicCity(e.target.value)}
                                    variant="standard"
                                    label="Ciudad"
                                    inputProps={{ readOnly: !editClinicData }}
                                  />
                                </Grid>
                                  
                                <Grid item xs={12} md={6}>
                                  <TextField
                                    fullWidth
                                    value={clinicDomicilio}
                                    onChange={(e) => setClinicDomicilio(e.target.value)}
                                    variant="standard"
                                    label="Dirección"
                                    inputProps={{ readOnly: !editClinicData }}
                                  />
                                </Grid>
                                  
                                <Grid item xs={12} md={6}>
                                  <TextField
                                    fullWidth
                                    value={clinicEmail}
                                    onChange={(e) => setClinicEmail(e.target.value)}
                                    variant="standard"
                                    label="Email"
                                    inputProps={{ readOnly: !editClinicData }}
                                  />
                                </Grid>
                                  
                                <Grid item xs={12} md={6}>
                                  <TextField
                                    fullWidth
                                    value={clinicTelefono}
                                    onChange={(e) => setClinicTelefono(e.target.value)}
                                    variant="standard"
                                    label="Teléfono"
                                    helperText={editClinicData && paisClinica !== 'ES' ? 'Indica el prefijo (+...) o se asumirá el de tu país' : undefined}
                                    inputProps={{ readOnly: !editClinicData }}
                                  />
                                </Grid>
                              </Grid>
                            </Card>

                            {/* SALAS */}
                            <Card sx={{ p: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="h6" fontWeight="bold">
                                  Salas
                                </Typography>
                                                              
                                <Button size="small" variant="contained" onClick={() => setModalSala(true)}>
                                  Añadir
                                </Button>
                              </Box>
                                                              
                              {salas.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                  No hay salas creadas
                                </Typography>
                              ) : (
                                <Stack spacing={1.5} sx={{ mt: 2 }}>
                                  {salas.map((sala: ISala, index: number) => {
                                    
                                    let icon;
                                    switch (sala.icon) {
                                      case "0": icon = <WavesIcon fontSize="small" />; break;
                                      case "1": icon = <DiamondIcon fontSize="small" />; break;
                                      case "2": icon = <LandscapeIcon fontSize="small" />; break;
                                      case "3": icon = <GrassIcon fontSize="small" />; break;
                                      case "4": icon = <AcUnitIcon fontSize="small" />; break;
                                    }
                                  
                                    return (
                                      <Card
                                        key={index}
                                        variant="outlined"
                                        sx={{
                                          p: 1.5,
                                          cursor: 'pointer',
                                          '&:hover': { bgcolor: 'action.hover' }
                                        }}
                                        onClick={() => setIsUpdateSala(sala)}
                                      >
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 2
                                          }}
                                        >
                                          {/* IZQUIERDA: ICONO + NOMBRE */}
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                            {icon}
                                            <Typography variant="subtitle2" noWrap>
                                              {sala.title}
                                            </Typography>
                                          </Box>
                                        
                                          {/* DERECHA: CHIPS */}
                                          {sala.actividades?.length > 0 && (
                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                              {sala.actividades.map((actividad: string) => (
                                                <Chip
                                                  key={actividad}
                                                  size="small"
                                                  label={actividades.find(a => a._id === actividad)?.title}
                                                />
                                              ))}
                                            </Box>
                                          )}
                                        </Box>
                                      </Card>
                                    );
                                  })}
                                </Stack>
                              )}
                            </Card>
                                
                            {/* ACTIVIDADES */}
                            <Card sx={{ p: 3, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="h6" fontWeight="bold">
                                  Actividades
                                </Typography>
                            
                                <Button size="small" variant="contained" onClick={() => setModalActividad(true)}>
                                  Añadir
                                </Button>
                              </Box>
                            
                              {actividades.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                  No hay actividades creadas
                                </Typography>
                              ) : (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 1 }}>
                                  {actividades.map((actividad: IActividad) => (
                                    <Chip
                                      key={actividad._id}
                                      label={actividad.title}
                                      onClick={() => setIsUpdateActividad(actividad)}
                                      sx={{ m: 0.5 }}
                                    />
                                  ))}
                                </Box>
                              )}
                            </Card>
                          </Stack>
                        </Grid>

                        <Grid item xs={12} md={5}>
                          <Stack spacing={3}>

                            {/* RECORDATORIOS */}
                            <Card sx={{ p: 3 }}>
                              <Typography variant="h6" fontWeight="bold">
                                Recordatorios de citas
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                                Envío automático de recordatorios por email el día antes de la cita
                              </Typography>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={citaReminder as boolean}
                                    onChange={(e) => {
                                      const newValue = e.target.checked;
                                      if (!newValue) {
                                        setPendingReminderValue(newValue);
                                        setOpenReminderDialog(true);
                                      } else {
                                        updateReminderConfig(true);
                                      }
                                    }}
                                  />
                                }
                                label={
                                  loadingReminder
                                    ? "Guardando..."
                                    : citaReminder
                                      ? "Activados"
                                      : "Desactivados"
                                }
                              />
                              {/* Nota contextual según estado de WA */}
                              {citaReminder && waEnabled && (
                                <Alert severity="info" sx={{ mt: 2, fontSize: 12 }}>
                                  El email actúa como alternativa cuando no es posible el envío por WhatsApp.
                                </Alert>
                              )}
                              {citaReminder && !waEnabled && (
                                <Alert severity="info" sx={{ mt: 2, fontSize: 12 }}>
                                  Activa WhatsApp para enviar recordatorios por ese canal con mayor tasa de apertura.
                                </Alert>
                              )}
                            </Card>

                            {/* MODAL DE CONFIRMACIÓN PARA DESACTIVAR RECORDATORIOS */}
                            <Dialog
                              open={openReminderDialog}
                              onClose={() => setOpenReminderDialog(false)}
                            >
                              <DialogTitle>Desactivar recordatorios</DialogTitle>
                              <DialogContent>
                                <DialogContentText>
                                  {waEnabled
                                    ? 'Si desactivas los recordatorios por email, los pacientes solo recibirán recordatorios por WhatsApp. Si un envío por WA falla, no habrá alternativa.'
                                    : 'Si desactivas los recordatorios, los pacientes dejarán de recibir notificaciones antes de sus citas. Esta acción puede afectar a la asistencia.'
                                  }
                                </DialogContentText>
                              </DialogContent>
                              <DialogActions>
                                <Button onClick={() => setOpenReminderDialog(false)}>
                                  Cancelar
                                </Button>
                                <Button
                                  color="error"
                                  variant="contained"
                                  onClick={() => {
                                    if (pendingReminderValue !== null) {
                                      updateReminderConfig(pendingReminderValue);
                                    }
                                
                                    setOpenReminderDialog(false);
                                    setPendingReminderValue(null);
                                  }}
                                >
                                  Desactivar
                                </Button>
                              </DialogActions>
                            </Dialog>

                            {/* WHATSAPP */}
                            <Card sx={{ p: 3 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="h6" fontWeight="bold">
                                  Recordatorios por WhatsApp
                                </Typography>
                                <Chip
                                  label="Nuevo"
                                  size="small"
                                  color="success"
                                  sx={{ fontSize: 11, height: 20 }}
                                />
                              </Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Envío automático de recordatorios por WhatsApp el día antes de la cita.
                                {waEnabled && citaReminder && ' El email se usa como alternativa si el envío por WA no es posible.'}
                              </Typography>

                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={waEnabled as boolean}
                                    onChange={(e) => {
                                      const newValue = e.target.checked;
                                      if (!newValue) {
                                        setPendingWaValue(newValue);
                                        setOpenWaDialog(true);
                                      } else {
                                        updateWaConfig(true);
                                      }
                                    }}
                                  />
                                }
                                label={
                                  loadingWa
                                    ? 'Guardando...'
                                    : waEnabled
                                      ? 'Activado'
                                      : 'Desactivado'
                                }
                              />

                              {/* Aviso si WA activo pero email desactivado — sin fallback */}
                              {waEnabled && !citaReminder && (
                                <Alert severity="warning" sx={{ mt: 2, fontSize: 12 }}>
                                  Sin recordatorios por email activos. Si un envío por WhatsApp falla, el paciente no recibirá ningún aviso.
                                </Alert>
                              )}

                              {waEnabled && (
                                <Box sx={{ mt: 2 }}>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<WhatsAppIcon />}
                                    onClick={() => navigate(constantes.R_WHATSAPP)}
                                  >
                                    Ir al panel de WhatsApp
                                  </Button>
                                </Box>
                              )}
                            </Card>
                            
                            <Dialog
                              open={openWaDialog}
                              onClose={() => setOpenWaDialog(false)}
                            >
                              <DialogTitle>Desactivar WhatsApp</DialogTitle>
                              <DialogContent>
                                <DialogContentText>
                                  Si desactivas WhatsApp, los pacientes dejarán de recibir
                                  recordatorios por este canal. Los créditos disponibles se
                                  conservan para cuando lo reactives.
                                </DialogContentText>
                              </DialogContent>
                              <DialogActions>
                                <Button onClick={() => setOpenWaDialog(false)}>
                                  Cancelar
                                </Button>
                                <Button
                                  color="error"
                                  variant="contained"
                                  onClick={() => {
                                    if (pendingWaValue !== null) {
                                      updateWaConfig(pendingWaValue);
                                    }
                                    setOpenWaDialog(false);
                                    setPendingWaValue(null);
                                  }}
                                >
                                  Desactivar
                                </Button>
                              </DialogActions>
                            </Dialog>

                            {/* PERSONALIZACION */}
                            <Card sx={{ p: 3 }}>
                              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                                Personalización
                              </Typography>

                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={7}>
                                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                                    <ImageUploader
                                      aspect={1}
                                      label="Subir logo"
                                      initialImage={clinicLogo || null}
                                      onCropped={(img) => setClinicLogo(img)}
                                    />
                                  </Box>
                                </Grid>
                            
                                <Grid item xs={12} md={5}>
                                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                                    <ColorPicker
                                      color={clinicColor}
                                      onChange={(c) => setClinicColor(c)}
                                    />
                                  </Box>
                                </Grid>
                              </Grid>
                            
                              <Button
                                fullWidth
                                variant="contained"
                                color="success"
                                disabled={loadingStyle}
                                sx={{ mt: 3 }}
                                onClick={saveClinicStyle}
                              >
                                Guardar cambios
                              </Button>
                            
                            </Card>

                          </Stack>
                        </Grid>
                      </Grid>
                      </TabPanel>

                      <TabPanel value="3">
                        <ConfigAgenda />
                      </TabPanel>
                  
                      <TabPanel value="4">
                        <Servicios />
                      </TabPanel>
                  
                      <TabPanel value="5">
                        <Holidays />
                      </TabPanel>
                  
                      <TabPanel value="6">
                        <Mail />
                      </TabPanel>
                  
                      <TabPanel value="7">
                        <JA />
                      </TabPanel>
                  
                      <TabPanel value="8">
                        <CI />
                      </TabPanel>
                  
                    </TabContext>
                  
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 1,
                        opacity: 0.45
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "10px",
                          letterSpacing: 0.5,
                          color: "text.disabled",
                          userSelect: "none"
                        }}
                      >
                        v{APP_VERSION} · {apiVersion}
                      </Typography>
                    </Box>
                    
                  </Paper>
                </Container>
              </Grid>
            </Grid>

            {/* AÑADIR ACTIVIDAD */}
            <Dialog open={modalActividad} maxWidth="lg" fullWidth onClose={closeModalActividad}>
                    <DialogContent>
                        <Typography variant="h4" sx={{ display: 'inline-flex' }}>
                            <Avatar sx={{ margin: theme.spacing(1), backgroundColor: '#2e7d32' }}><AddIcon /></Avatar><div style={{ margin: '10px auto' }}>{isEditActividad ? 'Editar actividad' : 'Nueva actividad'}</div>
                        </Typography>

                        <Grid container spacing={1}>
                            <Grid item md={4} xs={12}>
                                <TextField value={actividadName} onChange={(e) => setActividadName(e.target.value)} variant="outlined" label="Nombre de la actividad" margin="normal" fullWidth autoFocus required />
                            </Grid>
                            <Grid item md={4} xs={12}>
                                <TextField select value={duracion} variant="outlined" label="Duración" margin="normal" fullWidth required onChange={(e) => setDuracion(parseInt(e.target.value))} >
                                    {
                                        constantes.duracion.map((item, index) => {
                                            return (
                                                <MenuItem key={index} value={item}>{`${item.toString()} minutos`}</MenuItem>
                                            );
                                        })
                                    }
                                </TextField>
                            </Grid>
                            <Grid item md={4} xs={12}>
                                <TextField select value={capacidad} variant="outlined" label="Capacidad" margin="normal" fullWidth required onChange={(e) => setCapacidad(parseInt(e.target.value))} >
                                    {
                                        constantes.capacidad.map((item, index) => {
                                            return (
                                                <MenuItem key={index} value={item}>{`${item.toString()} persona(s)`}</MenuItem>
                                            );
                                        })
                                    }
                                </TextField>
                            </Grid>
                        </Grid>
                    </DialogContent>
              {loadingActividad ?
                <Grid container>
                  <CircularProgress style={{ margin: '20px auto' }} />
                </Grid> :
                <DialogActions>
                  { isEditActividad && false && <Button color="error" onClick={() => setModalDeleteActividad(true)}>Eliminar</Button>}
                  <Button onClick={isEditActividad ? updateActividad : saveActividad}>Guardar</Button>
                  <Button onClick={closeModalActividad}>Cancelar</Button>
                </DialogActions>}
            </Dialog>

            {/* ELIMINAR ACTIVIDAD */}
            <Dialog open={modalDeleteActividad} maxWidth="lg" onClose={() => setModalDeleteActividad(false)}>
                <DialogTitle>ELIMINAR ACTIVIDAD</DialogTitle>
                <DialogContent>
                    <DialogContentText>{`¿Está seguro de que desea eliminar la actividad ${selectedActividad?.title}?`}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={deleteActividad} color="primary">SI</Button>
                    <Button onClick={() => { setModalDeleteActividad(false); setSelectedActividad(undefined) }} color="primary" autoFocus>NO</Button>
                </DialogActions>
            </Dialog>

            {/* AÑADIR SALA */}
            <Dialog open={modalSala} maxWidth="lg" fullWidth onClose={closeModalSala}>
                <DialogContent>
                    <Typography variant="h4" sx={{ display: 'inline-flex' }}>
                        <Avatar sx={{ margin: theme.spacing(1), backgroundColor: '#2e7d32' }}><AddIcon /></Avatar><div style={{ margin: '10px auto' }}>{isEditSala ? 'Editar sala' : 'Nueva sala'}</div>
                    </Typography>
                    <Grid container spacing={1}>
                        <Grid item md={4} xs={12}>
                            <TextField value={salaName} onChange={(e) => setSalaName(e.target.value)} variant="outlined" label="Nombre de la sala" margin="normal" fullWidth autoFocus required />
                        </Grid>
                        <Grid item md={3} xs={12}>
                            <FormControl>
                                <InputLabel id="icon" shrink>Selecciona un icono</InputLabel>
                                <Select value={salaIcon} onChange={handleChangeIcon} sx={{ mt: theme.spacing(2), width: "250px" }} required renderValue={(selected) => (<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>{constantes.icons[parseInt(selected)]}</Box>)}>
                                    <MenuItem value="0">
                                        <ListItemIcon>
                                            <WavesIcon />
                                        </ListItemIcon>
                                        <ListItemText>Agua</ListItemText>
                                    </MenuItem>
                                    <MenuItem value="1">
                                        <ListItemIcon>
                                            <DiamondIcon />
                                        </ListItemIcon>
                                        <ListItemText>Diamante</ListItemText>
                                    </MenuItem>
                                    <MenuItem value="2">
                                        <ListItemIcon>
                                            <LandscapeIcon />
                                        </ListItemIcon>
                                        <ListItemText>Montaña</ListItemText>
                                    </MenuItem>
                                    <MenuItem value="3">
                                        <ListItemIcon>
                                            <GrassIcon />
                                        </ListItemIcon>
                                        <ListItemText>Planta</ListItemText>
                                    </MenuItem>
                                    <MenuItem value="4">
                                        <ListItemIcon>
                                            <AcUnitIcon />
                                        </ListItemIcon>
                                        <ListItemText>Nieve</ListItemText>
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item md={5} xs={12}>
                            {actividades.length !== 0 && <FormControl>
                                <InputLabel id="acts" shrink>Actividades</InputLabel>
                                <Select label="Actividades" labelId="acts" sx={{ mt: theme.spacing(2), minWidth: '200px', width: 'fit-content' }} multiple value={salaActs} onChange={handleChangeSala} renderValue={(selected) => (<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>  {selected.filter((value) => actividades.some(a => a._id === value)).map((value) => (<Chip key={value} label={actividades.find(a => a._id === value)!.title} />))}</Box>)}>
                                    {
                                        actividades.map((item, index) => {
                                            return (
                                                <MenuItem key={index} value={item._id ? item._id : undefined}>{item.title.toString()}</MenuItem>
                                            );
                                        })
                                    }
                                </Select>
                            </FormControl>}
                        </Grid>
                    </Grid>
                </DialogContent>
                {loadingSala ?
                    <Grid container>
                        <CircularProgress style={{ margin: '20px auto' }} />
                    </Grid> :
                    <DialogActions>
                        { isEditSala && false &&  <Button color="error" onClick={() => setModalDeleteSala(true)}>Eliminar</Button>}
                        <Button onClick={isEditSala ? updateSala : saveSala}>Guardar</Button>
                        <Button onClick={closeModalSala}>Cancelar</Button>
                    </DialogActions>}
            </Dialog>

            {/* ELIMINAR SALA */}
            <Dialog open={modalDeleteSala} maxWidth="lg" onClose={() => setModalDeleteSala(false)}>
                <DialogTitle>ELIMINAR SALA</DialogTitle>
                <DialogContent>
                    <DialogContentText>{`¿Está seguro de que desea eliminar la sala ${selectedSala?.title}?`}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={deleteSala} color="primary">SI</Button>
                    <Button onClick={() => { setModalDeleteSala(false); setSelectedSala(undefined) }} color="primary" autoFocus>NO</Button>
                </DialogActions>
            </Dialog>

            {/* CONFIRMACIÓN CAMBIO DE PROVINCIA */}
            <Dialog open={modalEditProvincia} maxWidth="lg" onClose={closeModalProvincia}>
                <DialogTitle>MODIFICAR PROVINCIA</DialogTitle>
                <DialogContent>
                    <DialogContentText>Al modificar la provincia, se modificarán también los festivos. ¿Está seguro de que desea modificar la provincia?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => updateClinicData(false)} color="primary">SI</Button>
                    <Button onClick={closeModalProvincia} color="primary" autoFocus>NO</Button>
                </DialogActions>
            </Dialog>
            
            <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
                <Alert variant="filled" onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>{alert.msg}</Alert>
            </Snackbar>

          </Grid>
          :
          <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open>
            <Alert severity="warning" sx={{ width: '100%' }}>
              Usuario no autorizado
            </Alert>
          </Snackbar>
        );
    } catch (e: any) {
        const error: ILog = { method: 'Config.Component', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return (
            <Alert severity="error">{e.message}</Alert>
        );
    }

};

export default Config;